/**
 * 🌍 Geolocation Utilities
 * Google Maps 기반 거리 계산 및 위치 서비스
 */

// Haversine formula로 두 GPS 좌표 간 거리 계산 (km)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // 지구 반지름 (km)
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return Math.round(distance * 10) / 10; // 소수점 첫째자리까지
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// 브라우저 Geolocation API로 현재 위치 가져오기
export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
}

// 주소 정보 타입
export interface GeocodeResult {
  lat: number;
  lng: number;
  city?: string;
  state?: string;
  postcode?: string;
  formattedAddress?: string;
}

// 주소를 GPS 좌표로 변환 (Google Geocoding API)
export async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.error("Google Maps API key not configured");
    console.error("Environment:", import.meta.env.MODE);
    console.error("All VITE_ vars:", Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')));
    return null;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&region=au&key=${apiKey}`
    );

    if (!response.ok) {
      throw new Error("Geocoding request failed");
    }

    const data = await response.json() as {
      status: string;
      results?: Array<{
        geometry: {
          location: { lat: number; lng: number }
        };
        address_components?: Array<{
          long_name: string;
          short_name: string;
          types: string[];
        }>;
        formatted_address?: string;
      }>
    };

    if (data.status === "OK" && data.results && data.results.length > 0) {
      const result = data.results[0];
      const location = result.geometry.location;

      // 주소 컴포넌트 파싱
      let city: string | undefined;
      let state: string | undefined;
      let postcode: string | undefined;

      if (result.address_components) {
        for (const component of result.address_components) {
          if (component.types.includes("locality") || component.types.includes("sublocality") || component.types.includes("postal_town")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.short_name;
          }
          if (component.types.includes("postal_code")) {
            postcode = component.long_name;
          }
        }
      }

      return {
        lat: location.lat,
        lng: location.lng,
        city,
        state,
        postcode,
        formattedAddress: result.formatted_address,
      };
    }

    return null;
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
}

// 호주 주요 도시 중심 좌표
export const australianCities = {
  sydney: { lat: -33.8688, lng: 151.2093, name: "Sydney" },
  melbourne: { lat: -37.8136, lng: 144.9631, name: "Melbourne" },
  brisbane: { lat: -27.4698, lng: 153.0251, name: "Brisbane" },
  perth: { lat: -31.9505, lng: 115.8605, name: "Perth" },
  adelaide: { lat: -34.9285, lng: 138.6007, name: "Adelaide" },
  canberra: { lat: -35.2809, lng: 149.1300, name: "Canberra" },
  hobart: { lat: -42.8821, lng: 147.3272, name: "Hobart" },
  darwin: { lat: -12.4634, lng: 130.8456, name: "Darwin" },
} as const;

// 한글 도시명 → 영어 도시명 매핑
const koreanCityNameMap: Record<string, string> = {
  // 주요 도시
  "시드니": "Sydney",
  "멜버른": "Melbourne",
  "멜번": "Melbourne",
  "브리즈번": "Brisbane",
  "퍼스": "Perth",
  "애들레이드": "Adelaide",
  "캔버라": "Canberra",
  "호바트": "Hobart",
  "다윈": "Darwin",
  // 기타 도시
  "뉴캐슬": "Newcastle",
  "울릉공": "Wollongong",
  "질롱": "Geelong",
  "골드코스트": "Gold Coast",
  "케언스": "Cairns",
  "라운세스톤": "Launceston",
  "데본포트": "Devonport",
  "번베리": "Burnie",
  // 주요 교민 지역
  "이스트우드": "Eastwood",
  "스트라스필드": "Strathfield",
  "에핑": "Epping",
  "챗스우드": "Chatswood",
  "버우드": "Burwood",
  "리드컴": "Lidcombe",
  "캠시": "Campsie",
  "로즈": "Rhodes",
  "파라마타": "Parramatta",
  "글렌 웨이벌리": "Glen Waverley",
  "박스 힐": "Box Hill",
  "돈캐스터": "Doncaster",
  "클레이튼": "Clayton",
  "번두라": "Bundoora",
  "써니뱅크": "Sunnybank",
  "어퍼 마운트 그래밧": "Upper Mount Gravatt",
  "투웡": "Toowong",
  "사우스포트": "Southport",
  "노우드": "Norwood",
  "프로스펙트": "Prospect",
  "캐닝 베일": "Canning Vale",
  "윌레튼": "Willetton",
  "벨코넨": "Belconnen",
};

/**
 * 한글 도시명을 영어로 변환 (있는 경우)
 * @param cityName 도시명 (한글 또는 영어)
 * @returns 영어 도시명 (변환 불가능하면 원본 반환)
 */
export function normalizeCityName(cityName: string): string {
  const trimmed = cityName.trim();
  // 한글 도시명 매핑 확인
  if (koreanCityNameMap[trimmed]) {
    return koreanCityNameMap[trimmed];
  }
  // 이미 영어인 경우 그대로 반환
  return trimmed;
}

// 거리 옵션
export const distanceOptions = [
  { value: "all", label: "전체 지역", labelEn: "All areas" },
  { value: "5", label: "5km 이내", labelEn: "Within 5km" },
  { value: "10", label: "10km 이내", labelEn: "Within 10km" },
  { value: "20", label: "20km 이내", labelEn: "Within 20km" },
] as const;

export type DistanceOption = typeof distanceOptions[number]["value"];

// 사용자 위치 저장소 (LocalStorage)
const LOCATION_KEY = "ozk_user_location";

export interface UserLocation {
  latitude: number;
  longitude: number;
  suburb?: string;
  city?: string;
  state?: string;
  timestamp: number;
}

export function saveUserLocation(location: UserLocation): void {
  localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
}

export function getUserLocation(): UserLocation | null {
  const stored = localStorage.getItem(LOCATION_KEY);
  if (!stored) return null;

  try {
    const location = JSON.parse(stored);
    // 24시간 이상 지난 위치는 무효화
    if (Date.now() - location.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(LOCATION_KEY);
      return null;
    }
    return location;
  } catch {
    return null;
  }
}

export function clearUserLocation(): void {
  localStorage.removeItem(LOCATION_KEY);
}

// 거리 문자열 포맷
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)}m`;
  }
  return `${km.toFixed(1)}km`;
}
